/**
 * Domain configuration for team logos and branding
 * 
 * HOW TO ADD A NEW TEAM DOMAIN:
 * 
 * 1. Add logo assets under src/utils/assets/teams/:
 *    - yourdomain.png — stacked / signup branding (optional)
 *    - yourdomain_long.png — horizontal logo for auth / signup (light backgrounds)
 *    - yourdomain_nav.png — horizontal navbar logo for dark AppBar (e.g. iuh_nav.png)
 * 
 * 2. Import the logo at the top of this file:
 *    import yourDomainLogo from '../assets/teams/yourdomain.png';
 * 
 * 3. Add the domain URL to the domainURLs array below
 * 
 * 4. Add the logo mapping to domainLogos object:
 *    yourdomain: yourDomainLogo,
 * 
 * 5. Add logo style to logoStyles object (optional, defaults to dev style):
 *    yourdomain: { height: 18, marginTop: '6px' },
 * 
 * 6. Add AppBar background color to appBarColors if needed (optional):
 *    yourdomain: '#4689e0', // or use 'default' for #313131
 * 
 * Example:
 * - Domain: 'newteam.oxiwear.fitness'
 * - Logo file: src/utils/assets/teams/newteam.png
 * - Domain prefix will be: 'newteam' (extracted from domain)
 */

import lnpcLogo from '../assets/teams/lnpc.png';
import devLogo from '../assets/Whitelabling_white.png';
import liquid from '../assets/teams/liquid.png';
import stjohns from '../assets/teams/stjohns-logo.svg';
import jhu from '../assets/teams/jhu_long.png';
import cc from '../assets/teams/cc.png';
import sayf from '../assets/teams/sayf.png';
import urmc from '../assets/teams/urmc.png';
import urmc_white from '../assets/teams/urmc_white.png';
import iuh from '../assets/teams/iuh_long.png';
import iuhNav from '../assets/teams/iuh_nav.png';
import dusm from '../assets/teams/dusm.png';
import uab from '../assets/teams/uab.png';
import dusmLong from '../assets/teams/dusm_long.png';
import dusmNav from '../assets/teams/dusm_nav.png';
import uabLong from '../assets/teams/uab_long.png';
import uabNav from '../assets/teams/uab_nav.png';

// Domain URLs array - add new domains here
export const domainURLs = [
	'lnpc.oxiwear.fitness',
	'dev-lnpc.oxiwear.fitness',
	'msc.oxiwear.fitness',
	'sju.oxiwear.fitness',
	'jhu.oxiwear.fitness',
	'cch.oxiwear.com',
	'sayf.oxiwear.com',
	'teams.oxiwear.fitness',
	'medical-teams.oxiwear.com',
	'teams.oxiwear.com',
	'aspetar.oxiwear.com',
	'jhu.oxiwear.com',
	'urmc.oxiwear.com',
	'urmcstudy.oxiwear.com',
	'iuh.oxiwear.com',
	'dusm.oxiwear.com',
	'uab.oxiwear.com',
];

// Wide horizontal partner logos (e.g. URMC)
export const partnerNavLogoStyle = {
	height: 35,
	marginBottom: '6px',
	width: 'auto',
	maxWidth: 200,
	objectFit: 'contain',
	alignSelf: 'center',
	display: 'block',
};

// IUH long logo is wider; slightly taller slot avoids clipping "Health"
export const iuhPartnerNavLogoStyle = {
	...partnerNavLogoStyle,
	maxHeight: 36,
	height: 'auto',
	width: 'auto',
	maxWidth: 320,
	marginTop: '2px',
	marginBottom: 0,
	objectFit: 'contain',
	objectPosition: 'left center',
	display: 'block',
	alignSelf: 'center',
};

// Logo mapping - maps domain prefix to logo image
// The domain prefix is extracted from the domain (e.g., 'lnpc' from 'lnpc.oxiwear.fitness')
export const domainLogos = {
	dev: devLogo,
	lnpc: lnpcLogo,
	'dev-lnpc': lnpcLogo,
	msc: liquid,
	sju: stjohns,
	jhu: jhu,
	urmc: urmc,
	urmcstudy: urmc,
	iuh: iuh,
	dusm: dusm,
	uab: uab,
	cch: cc,
	sayf: sayf,
	aspetar: devLogo, // Default for aspetar domain
	teams: devLogo, // Default for teams domains
	'medical-teams': devLogo, // Default for medical-teams domain
};

// Auth / login logos — separate transparent long variants when available
export const domainAuthLogos = {
	dusm: dusmLong,
	uab: uabLong,
};

// DUSM navbar: dark blue wordmark needs white backing on dark AppBar
export const dusmPartnerNavLogoStyle = {
	...partnerNavLogoStyle,
	height: 40,
	maxHeight: 48,
	maxWidth: 320,
	marginTop: '0px',
	marginBottom: '0px',
	padding: '2px 6px',
	backgroundColor: '#ffffff',
	// borderRadius: '4px',
	objectFit: 'contain',
	objectPosition: 'center',
};

// UAB navbar: wordmark only (no UB monogram), slight white backing
export const uabPartnerNavLogoStyle = {
	...partnerNavLogoStyle,
	height: 34,
	maxHeight: 34,
	maxWidth: 190,
	marginTop: '2px',
	marginBottom: '4px',
	padding: '3px 8px',
	backgroundColor: '#ffffff',
	borderRadius: '4px',
	objectFit: 'contain',
	objectPosition: 'center',
};

// Domains that use the wider auth logo slot on login / register
export const AUTH_PARTNER_WIDE_LOGO_DOMAINS = [
	'urmc',
	'urmcstudy',
	'iuh',
	'dusm',
	'uab',
];

// Navbar logos on dark AppBar — override when wordmark needs light text (e.g. IUH)
export const domainNavLogos = {
	iuh: iuhNav,
	dusm: dusmNav,
	uab: uabNav,
	urmc: urmc_white, // URMC logo is already light text, no override needed
};

// Logo styles configuration - customize logo display styles per domain
export const logoStyles = {
	dev: { height: 18, marginTop: '6px' },
	lnpc: { height: 18, marginTop: '6px' },
	'dev-lnpc': { height: 18, marginTop: '6px' },
	msc: { height: 18, marginTop: '6px' },
	sju: { height: 34, backgroundColor: 'white' },
	jhu: { height: 34, backgroundColor: 'white' },
	cch: { height: 34, backgroundColor: 'white' },
	sayf: { height: 34, backgroundColor: 'white' },
	teams: { height: 18, marginTop: '6px' },
	'medical-teams': { height: 18, marginTop: '6px' },
	urmc: partnerNavLogoStyle,
	urmcstudy: partnerNavLogoStyle,
	iuh: iuhPartnerNavLogoStyle,
	dusm: dusmPartnerNavLogoStyle,
	uab: uabPartnerNavLogoStyle,
	aspetar: { height: 18, marginTop: '6px' },
};

// Background color configuration for AppBar per domain
export const appBarColors = {
	msc: '#4689e0',
	default: '#313131',
};

// Domains that show partner logo on auth / register left panel
export const AUTH_TEAM_LOGO_DOMAINS = [
	'dev',
	'lnpc',
	'msc',
	'sju',
	'jhu',
	'cch',
	'sayf',
	'urmc',
	'urmcstudy',
	'iuh',
	'dusm',
	'uab',
];

// Helper function to get domain prefix from hostname
export const getDomainPrefix = (hostname) => {
	if (domainURLs.includes(hostname)) {
		return hostname.split('.')[0];
	}
	return 'dev'; // Default
};

// Helper function to get logo for a domain prefix
export const getLogoForDomain = (domainPrefix) => {
	return domainLogos[domainPrefix] || domainLogos.dev;
};

// Auth/login logo (prefers transparent long variant when configured)
export const getAuthLogoForDomain = (domainPrefix) => {
	return domainAuthLogos[domainPrefix] || getLogoForDomain(domainPrefix);
};

// Navbar logo (dark AppBar); falls back to standard domain logo
export const getNavLogoForDomain = (domainPrefix) => {
	return domainNavLogos[domainPrefix] || getLogoForDomain(domainPrefix);
};

// Helper function to get logo style for a domain prefix
export const getLogoStyleForDomain = (domainPrefix) => {
	return logoStyles[domainPrefix] || logoStyles.dev;
};

// Helper function to get AppBar color for a domain prefix
export const getAppBarColorForDomain = (domainPrefix) => {
	return appBarColors[domainPrefix] || appBarColors.default;
};



